import { Caption1, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ChevronDown12Regular, MoreHorizontal16Regular } from "@fluentui/react-icons";
import { useState, type ReactNode } from "react";
import { ConfirmDialog } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { FuiFilterSelect } from "@/components/fui-control";
import { ListToolbar } from "@/components/list-toolbar";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { useDialogLayer } from "@/components/ui/dialog-layer";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { formatAgo, formatDate } from "@/lib/date-format";
import { useProjectStore } from "@/store/project-store";
import { assignableRoles, cancelRequestImpact, expireDueInvites, isRoleDemotion, maskEmail, memberMatchesRole, memberMatchesStatus, reactivateImpactSentence, removalImpactSentence, resendImpactSentence, roleInviteLine, suspendImpactSentence, type MockMember } from "./mock-plan-access";
import { PersonAvatar } from "./PersonAvatar";

const useStyles = makeStyles({
  stack: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
  frame: {
    width: "100%",
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    borderTop: "none",
  },
  fixed: { tableLayout: "fixed" },
  clip: { overflow: "hidden" },
  person: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
  },
  personText: { minWidth: 0, flex: 1 },
  name: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  meta: {
    display: "block",
    maxWidth: "100%",
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  center: { textAlign: "center" },
  quiet: { color: tokens.colorNeutralForeground3 },
  popover: {
    zIndex: 100,
    pointerEvents: "auto",
  },
  /** 범위·필터는 글자가 잘리지 않게 줄이지 않는다. */
  solid: { flexShrink: 0 },
  roleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    maxWidth: "100%",
    height: "24px",
    padding: "0 4px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  roleCopy: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px",
    maxWidth: "220px",
    whiteSpace: "normal",
  },
  menuBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
});

const ROLE_FILTERS = ["전체", "소유자", "관리자", "편집자", "기여자", "뷰어"].map((value) => ({ value, label: value }));

const STATUS_FILTER_ORDER = ["활성", "중지", "초대 중", "요청 중", "수락", "거절", "만료", "취소", "접근 요청", "요청 거절", "차단"];

function statusFilterOptions(people: MockMember[]) {
  const present = new Set(people.map((person) => person.status));
  const ordered = STATUS_FILTER_ORDER.filter((status) => present.has(status));
  for (const status of present) {
    if (!ordered.includes(status)) ordered.push(status);
  }
  return [{ value: "전체", label: "전체" }, ...ordered.map((value) => ({ value, label: value }))];
}

type Pending =
  | { kind: "remove"; person: MockMember }
  | { kind: "suspend"; person: MockMember }
  | { kind: "reactivate"; person: MockMember }
  | { kind: "cancel"; person: MockMember }
  | { kind: "resend"; person: MockMember }
  | { kind: "role"; person: MockMember; role: string }
  | { kind: "cancelRequest"; person: MockMember };

export type MemberListTab = "members" | "invites" | "requests";

const CHANGEABLE = new Set(["활성", "수락", "초대 중"]);

/** 플랜 멤버 표. 역할·상태 필터, 제거·역할·초대 취소·접근 요청 취소를 담는다. */
export function MemberTable({
  people,
  actorRole,
  leading,
  action,
  listEmpty,
  onRemove,
  onSuspend,
  onReactivate,
  onCancelInvite,
  onResend,
  onRole,
  onCancelRequest,
  teamOf,
}: {
  people: MockMember[];
  /** 있으면 내 역할 이하로 역할을 고를 수 있다. */
  actorRole?: string;
  /** 툴바 왼쪽. 멤버·초대·접근 요청처럼 목록 범위. */
  leading?: ReactNode;
  /** 툴바 오른쪽의 본동사. */
  action?: ReactNode;
  /** 사람이 한 명도 없을 때의 빈 상태. 없으면 필터가 비운 것으로 본다. */
  listEmpty?: { title: string; description: string };
  onRemove?: (id: string) => void;
  onSuspend?: (id: string) => void;
  onReactivate?: (id: string) => void;
  onCancelInvite?: (id: string) => void;
  onResend?: (id: string) => void;
  onRole?: (id: string, role: string) => void;
  onCancelRequest?: (id: string) => void;
  /** 워크스페이스 소속 팀. 있으면 읽기 전용 칸으로 보여 준다. */
  teamOf?: (person: MockMember) => string;
}) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const layer = useDialogLayer();
  const maskPeople = useProjectStore((s) => s.previewGuest);
  const [pending, setPending] = useState<Pending | null>(null);
  const [role, setRole] = useState("전체");
  const [status, setStatus] = useState("전체");
  const roles = actorRole && onRole ? assignableRoles(actorRole) : [];
  const menuProps = { mountNode: layer, positioning: { strategy: "fixed" as const, position: "below" as const, align: "end" as const } };
  const shown = people.filter(
    (person) => memberMatchesRole(person, role) && memberMatchesStatus(person, status),
  );

  return (
    <>
      <div className={styles.stack}>
        <ListToolbar
          edge="table"
          filters={
            <>
              {leading}
              <FuiFilterSelect
                className={styles.solid}
                density="dialog"
                label="역할"
                ariaLabel="역할 필터"
                value={role}
                options={ROLE_FILTERS}
                onValueChange={setRole}
                mountNode={layer}
              />
              <FuiFilterSelect
                className={styles.solid}
                density="dialog"
                label="상태"
                ariaLabel="상태 필터"
                value={status}
                options={statusFilterOptions(people)}
                onValueChange={setStatus}
                mountNode={layer}
              />
            </>
          }
          trail={action}
        />
        {people.length === 0 && listEmpty ? (
          <GhostEmptyState compact title={listEmpty.title} description={listEmpty.description} />
        ) : shown.length === 0 ? (
          <GhostEmptyState
            compact
            title="일치하는 사람이 없습니다"
            description="역할이나 상태를 바꿔 보세요."
          />
        ) : (
      <div className={mergeClasses(table.frame, styles.frame)}>
        <table className={mergeClasses(table.table, styles.fixed)} aria-label="멤버">
          <thead>
            <tr>
              <th className={table.th}>이름</th>
              <th className={table.th} style={{ width: "88px" }}>역할</th>
              {teamOf ? <th className={table.th} style={{ width: "120px" }}>팀</th> : null}
              <th className={mergeClasses(table.th, styles.center)} style={{ width: "72px" }}>상태</th>
              <th className={mergeClasses(table.th, table.num)} style={{ width: "96px" }}>마지막 활동</th>
              <th className={mergeClasses(table.th, table.num)} style={{ width: "96px" }}>참여일</th>
              <th className={mergeClasses(table.th, styles.center)} style={{ width: "72px" }}>동작</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((person) => {
              const actions = rowActions(person, onRemove, onSuspend, onReactivate, onCancelInvite, onResend, onCancelRequest);
              const email = person.email ? (maskPeople ? maskEmail(person.email) : person.email) : "";
              return (
                <tr key={person.id}>
                  <td className={mergeClasses(table.td, styles.clip)}>
                    <div className={styles.person}>
                      <PersonAvatar name={person.name} size={20} />
                      <div className={styles.personText}>
                        <div className={styles.name} title={person.name}>{person.name}</div>
                        {email ? <Caption1 className={styles.meta} title={email}>{email}</Caption1> : null}
                      </div>
                    </div>
                  </td>
                  <td className={table.td}>
                    {roles.length > 0 && person.role !== "소유자" && CHANGEABLE.has(person.status) ? (
                      <Menu {...menuProps}>
                        <MenuTrigger disableButtonEnhancement>
                          <button type="button" className={styles.roleBtn} aria-label={`${person.name} 역할, ${person.role}`}>
                            <span className={styles.name}>{person.role}</span>
                            <ChevronDown12Regular />
                          </button>
                        </MenuTrigger>
                        <MenuPopover className={styles.popover}>
                          <MenuList>
                            {roles.map((role) => (
                              <MenuItem
                                key={role}
                                disabled={role === person.role}
                                onClick={() => pickRole(person, role, onRole, setPending)}
                              >
                                <span className={styles.roleCopy}>
                                  <span>{role}</span>
                                  {roleInviteLine(role) ? <Caption1 className={styles.quiet}>{roleInviteLine(role)}</Caption1> : null}
                                </span>
                              </MenuItem>
                            ))}
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    ) : (
                      <div className={styles.name} title={person.role}>{person.role}</div>
                    )}
                  </td>
                  {teamOf ? (
                    <td className={table.td}>
                      <div className={styles.name} title={teamOf(person) || undefined}>{teamOf(person) || "—"}</div>
                    </td>
                  ) : null}
                  <td className={mergeClasses(table.td, styles.center)}>{person.status}</td>
                  <td className={mergeClasses(table.td, table.num)}>
                    {person.lastActive ? formatAgo(person.lastActive) : "—"}
                  </td>
                  <td className={mergeClasses(table.td, table.num)}>{formatDate(person.joined, "numeric")}</td>
                  <td className={mergeClasses(table.td, styles.center)}>
                    {actions.length === 0 ? (
                      <Caption1 className={styles.quiet}>—</Caption1>
                    ) : (
                      <Menu {...menuProps}>
                        <MenuTrigger disableButtonEnhancement>
                          <button type="button" className={styles.menuBtn} aria-label={`${person.name} 동작`}>
                            <MoreHorizontal16Regular />
                          </button>
                        </MenuTrigger>
                        <MenuPopover className={styles.popover}>
                          <MenuList>
                            {actions.map((action) => (
                              <MenuItem key={action.kind} onClick={() => setPending({ kind: action.kind, person })}>
                                {action.label}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
        )}
      </div>
      <ConfirmDialog
        open={pending !== null}
        title={pendingTitle(pending)}
        message={pendingMessage(pending)}
        confirmLabel={pendingConfirm(pending)}
        cancelLabel={pending?.kind === "cancel" || pending?.kind === "cancelRequest" ? "닫기" : "취소"}
        tier={pending?.kind === "remove" ? "T2" : "T1"}
        expectedName={pending?.kind === "remove" ? pending.person.name : ""}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending?.kind === "remove") onRemove?.(pending.person.id);
          if (pending?.kind === "suspend") onSuspend?.(pending.person.id);
          if (pending?.kind === "reactivate") onReactivate?.(pending.person.id);
          if (pending?.kind === "cancel") onCancelInvite?.(pending.person.id);
          if (pending?.kind === "resend") onResend?.(pending.person.id);
          if (pending?.kind === "role") onRole?.(pending.person.id, pending.role);
          if (pending?.kind === "cancelRequest") onCancelRequest?.(pending.person.id);
          setPending(null);
        }}
      />
    </>
  );
}

function pickRole(
  person: MockMember,
  role: string,
  onRole: ((id: string, role: string) => void) | undefined,
  setPending: (next: Pending) => void,
) {
  if (!onRole || role === person.role) return;
  if (person.id === "me" || isRoleDemotion(person.role, role)) {
    setPending({ kind: "role", person, role });
    return;
  }
  onRole(person.id, role);
}

function pendingTitle(pending: Pending | null) {
  if (pending?.kind === "remove") return "멤버 제거";
  if (pending?.kind === "suspend") return "멤버 중지";
  if (pending?.kind === "reactivate") return "멤버 재활성";
  if (pending?.kind === "role") return "역할 변경";
  if (pending?.kind === "cancelRequest") return "접근 요청 취소";
  if (pending?.kind === "cancel") return "초대 취소";
  if (pending?.kind === "resend") return "초대 재발송";
  return "";
}

function pendingConfirm(pending: Pending | null) {
  if (pending?.kind === "remove") return "제거";
  if (pending?.kind === "suspend") return "중지";
  if (pending?.kind === "reactivate") return "재활성";
  if (pending?.kind === "role") return "변경";
  if (pending?.kind === "cancel" || pending?.kind === "cancelRequest") return "취소";
  if (pending?.kind === "resend") return "재발송";
  return "확인";
}

function pendingMessage(pending: Pending | null) {
  if (!pending) return "";
  if (pending.kind === "remove") {
    return <>이 멤버를 이 플랜에서 제거할까요? {removalImpactSentence()}</>;
  }
  if (pending.kind === "suspend") {
    return <>이 멤버를 중지할까요? {suspendImpactSentence()}</>;
  }
  if (pending.kind === "reactivate") {
    return <>이 멤버를 다시 활성으로 둘까요? {reactivateImpactSentence()}</>;
  }
  if (pending.kind === "role") {
    return isRoleDemotion(pending.person.role, pending.role) ? (
      <>역할을 낮출까요? 이 플랜에서 할 수 있는 일이 줄어듭니다.</>
    ) : (
      <>역할을 바꿀까요?</>
    );
  }
  if (pending.kind === "cancelRequest") {
    return <>이 접근 요청을 취소할까요? {cancelRequestImpact()}</>;
  }
  if (pending.kind === "resend") {
    return <>초대를 다시 보낼까요? {resendImpactSentence()}</>;
  }
  return <>초대를 취소할까요?</>;
}

type RowActionKind = Exclude<Pending["kind"], "role">;

function rowActions(
  person: MockMember,
  onRemove?: (id: string) => void,
  onSuspend?: (id: string) => void,
  onReactivate?: (id: string) => void,
  onCancelInvite?: (id: string) => void,
  onResend?: (id: string) => void,
  onCancelRequest?: (id: string) => void,
): { kind: RowActionKind; label: string }[] {
  if (person.status === "요청 중" && onCancelRequest) {
    return [{ kind: "cancelRequest", label: "취소" }];
  }
  if (person.status === "초대 중" && (onResend || onCancelInvite)) {
    return [
      ...(onResend ? [{ kind: "resend" as const, label: "재발송" }] : []),
      ...(onCancelInvite ? [{ kind: "cancel" as const, label: "취소" }] : []),
    ];
  }
  if (person.status === "중지" && person.role !== "소유자" && onReactivate) {
    return [{ kind: "reactivate", label: "재활성" }];
  }
  if (person.status === "활성" && person.role !== "소유자" && (onRemove || onSuspend)) {
    return [
      ...(onSuspend ? [{ kind: "suspend" as const, label: "중지" }] : []),
      ...(onRemove ? [{ kind: "remove" as const, label: "제거" }] : []),
    ];
  }
  return [];
}

const LIST_EMPTY: Record<MemberListTab, { title: string; description: string }> = {
  members: { title: "아직 멤버가 없습니다", description: "초대를 보내면 여기에 모입니다." },
  invites: { title: "아직 초대가 없습니다", description: "초대를 보내면 수락 전에 여기에 있습니다." },
  requests: { title: "아직 접근 요청이 없습니다", description: "링크로 들어온 요청이 여기에 모입니다." },
};

/** 멤버·초대·접근 요청. 접근 요청 탭은 권한이 있을 때만 보이고, 라벨에 건수를 적는다. */
export function MemberRoster({
  members,
  invites,
  requests,
  canReview,
  listTab,
  onListTab,
  actorRole,
  memberEmptyDescription,
  toolbarAction,
  onRemove,
  onSuspend,
  onReactivate,
  onCancelInvite,
  onResend,
  onRole,
  onCancelRequest,
  teamOf,
}: {
  members: MockMember[];
  invites: MockMember[];
  requests: MockMember[];
  canReview: boolean;
  listTab: MemberListTab;
  onListTab: (tab: MemberListTab) => void;
  actorRole?: string;
  memberEmptyDescription?: string;
  /** 목록 툴바 오른쪽의 본동사. */
  toolbarAction?: ReactNode;
  onRemove?: (id: string) => void;
  onSuspend?: (id: string) => void;
  onReactivate?: (id: string) => void;
  onCancelInvite?: (id: string) => void;
  onResend?: (id: string) => void;
  onRole?: (id: string, role: string) => void;
  onCancelRequest?: (id: string) => void;
  /** 워크스페이스 소속. 멤버 표에만 읽기로 둔다. */
  teamOf?: (person: MockMember) => string;
}) {
  const styles = useStyles();
  const current: MemberListTab = !canReview && listTab === "requests" ? "members" : listTab;
  const options = [
    { value: "members" as const, label: "멤버" },
    { value: "invites" as const, label: "초대" },
    ...(canReview ? [{ value: "requests" as const, label: `접근 요청 ${requests.length}` }] : []),
  ];
  const people = current === "members" ? members : current === "invites" ? invites : requests;
  const empty = LIST_EMPTY[current];
  const description = current === "members" && memberEmptyDescription ? memberEmptyDescription : empty.description;

  return (
    <div className={styles.stack}>
      <MemberTable
        key={current}
        people={people}
        actorRole={actorRole}
        listEmpty={people.length === 0 ? { title: empty.title, description } : undefined}
        action={toolbarAction}
        teamOf={current === "members" ? teamOf : undefined}
        leading={
          <SegmentedControl
            className={styles.solid}
            ariaLabel="멤버 목록"
            value={current}
            onChange={(next) => {
              if (next === "invites") expireDueInvites();
              onListTab(next);
            }}
            options={options}
          />
        }
        onRemove={current === "members" ? onRemove : undefined}
        onSuspend={current === "members" ? onSuspend : undefined}
        onReactivate={current === "members" ? onReactivate : undefined}
        onCancelInvite={current === "invites" ? onCancelInvite : undefined}
        onResend={current === "invites" ? onResend : undefined}
        onRole={current === "requests" ? undefined : onRole}
        onCancelRequest={current === "requests" ? onCancelRequest : undefined}
      />
    </div>
  );
}
